import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { 
  Notification,
  getNotifications,
  markAsRead,
  markAllAsRead,
  deleteAllNotifications
} from '../api/notifications';

export const useNotifications = (unread_only: boolean = false) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keycloak } = useKeycloak();

  // Load notifications
  const loadNotifications = async () => {
    if (!keycloak.authenticated || !keycloak.token) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await getNotifications(keycloak.token, unread_only);
      setNotifications(response.results);
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Mark as read
  const handleMarkAsRead = async (notificationId: string) => {
    if (!keycloak.token) return;
    
    try {
      await markAsRead(keycloak.token, notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
    } catch (err: any) {
      console.error('📢 Error marking as read:', err);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    if (!keycloak.token) return;
    
    try {
      await markAllAsRead(keycloak.token);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
    } catch (err: any) {
      console.error('📢 Error marking all as read:', err);
    }
  };

  // Delete all
  const handleDeleteAll = async () => {
    if (!keycloak.token) return;
    
    try {
      await deleteAllNotifications(keycloak.token);
      setNotifications([]);
    } catch (err: any) {
      console.error('📢 Error deleting all:', err);
    }
  };

  // Load on mount
  useEffect(() => {
    loadNotifications();
  }, [keycloak.authenticated, unread_only]);

  // Count of unread notifications
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return {
    notifications,
    loading,
    error,
    unreadCount,
    refresh: loadNotifications,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    deleteAll: handleDeleteAll
  };
}; 